// src/components/Features.tsx
import React from 'react';
import { Users, Palette, Layout, RefreshCw, Mail } from 'lucide-react';

interface FeatureProps {
  icon: React.ReactNode;
  title: string;
  description: string;
}

const Feature: React.FC<FeatureProps> = ({ icon, title, description }) => (
  <div className="rounded-lg bg-white p-6 border border-gray-200 shadow-md hover:shadow-lg transition-shadow">
    <div className="mb-4 flex items-center space-x-3">
      <div className="rounded-full bg-blue-100 p-3">
        {icon}
      </div>
      <h3 className="text-xl font-semibold text-gray-900">{title}</h3>
    </div>
    <p className="text-gray-600">{description}</p>
  </div>
);

const SignatureExample = () => (
  <div className="relative mx-auto max-w-4xl overflow-hidden rounded-lg bg-white p-4 shadow-lg mb-16">
    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
      <div className="p-4">
        <div className="mb-3 text-red-600 text-sm font-semibold uppercase">Before SignoBox</div>
        <div className="rounded border border-gray-200 p-4">
          <div className="border-b border-gray-200 pb-3">
            <div className="font-bold">John Smith</div>
            <div className="text-gray-500 text-sm">Sales Representative</div>
          </div>
          <div className="pt-3 text-sm">
            <div>j.smith@company.com</div>
            <div>555-123-4567</div>
            <div className="mt-2 text-xs text-gray-400">Sent from my iPhone</div>
          </div>
        </div>
        <div className="mt-4 text-sm text-gray-500">
          ❌ Inconsistent branding<br />
          ❌ Missing company details<br />
          ❌ No social media links
        </div>
      </div>
      
      <div className="p-4">
        <div className="mb-3 text-green-600 text-sm font-semibold uppercase">With SignoBox</div>
        <div className="rounded border border-gray-200 p-4">
          <div className="border-b border-gray-200 pb-3 flex items-start">
            {/* Professional Headshot Photo */}
            <div className="mr-3 flex-shrink-0">
              <img 
                src="/images/profile-photo.jpg" 
                alt="John Smith" 
                className="h-16 w-16 rounded-full object-cover border-2 border-blue-100"
              />
            </div>
            <div>
              <div className="font-bold text-gray-900">John Smith</div>
              <div className="text-blue-600 text-sm">Senior Sales Representative</div>
              <div className="text-gray-500 text-xs">Enterprise Solutions Team</div>
            </div>
          </div>
          <div className="pt-3 text-sm space-y-1">
            <div className="flex items-center">
              <Mail className="h-4 w-4 mr-2 text-blue-600" />
              <span>john.smith@company.com</span>
            </div>
            <div className="flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-blue-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" />
              </svg>
              <span>(555) 123-4567</span>
            </div>
            <div className="flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-blue-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
              <span>123 Business Avenue, New York, NY 10001</span>
            </div>
            <div className="mt-3 flex space-x-2">
              <a href="#" className="h-6 w-6 rounded-full bg-blue-600 flex items-center justify-center text-white">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z" />
                </svg>
              </a>
              <a href="#" className="h-6 w-6 rounded-full bg-blue-800 flex items-center justify-center text-white">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M4.98 3.5c0 1.381-1.11 2.5-2.48 2.5s-2.48-1.119-2.48-2.5c0-1.38 1.11-2.5 2.48-2.5s2.48 1.12 2.48 2.5zm.02 4.5h-5v16h5v-16zm7.982 0h-4.968v16h4.969v-8.399c0-4.67 6.029-5.052 6.029 0v8.399h4.988v-10.131c0-7.88-8.922-7.593-11.018-3.714v-2.155z" />
                </svg>
              </a>
            </div>
            <div className="mt-3 pt-2 border-t border-gray-200 flex items-center">
              <div className="mr-3">
                <div className="h-8 w-8 rounded bg-blue-600 flex items-center justify-center text-white font-bold">
                  C
                </div>
              </div>
              <div className="text-xs text-gray-500">
                Company Inc. | Transforming businesses since 2010
              </div>
            </div>
          </div>
        </div>
        <div className="mt-4 text-sm text-gray-500">
          ✅ Professional photo and branding<br />
          ✅ Up-to-date role from HR system<br />
          ✅ Complete contact information and social links
        </div>
      </div>
    </div>
  </div>
);

const Features = () => {
  const features = [
    {
      icon: <Users className="h-6 w-6 text-blue-600" />,
      title: "HR System Integration",
      description: "Automatically pull additional employee data from your favourite HR tool and keep signatures up-to-date as roles or any employee info change"
    },
    {
      icon: <Palette className="h-6 w-6 text-blue-600" />,
      title: "Drag-and-Drop Template Builder",
      description: "Create beautiful, branded email signatures with an intuitive visual editor"
    },
    {
      icon: <Layout className="h-6 w-6 text-blue-600" />,
      title: "Centralized Admin Control",
      description: "Manage signatures across your entire organization from a single dashboard"
    },
    {
      icon: <RefreshCw className="h-6 w-6 text-blue-600" />,
      title: "Seamless Gmail Integration",
      description: "Deploy signature updates to employee accounts automatically"
    }
  ];

  return (
    <>
      <section className="container mx-auto px-6 py-16 bg-blue-50">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Transform Your Email Signatures</h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            See the difference SignoBox makes to your organization's professional appearance
          </p>
        </div>
        
        <SignatureExample />
      </section>
      
      <section className="container mx-auto px-6 py-16">
        <h2 className="text-3xl font-bold text-center mb-12 text-gray-900">Key Features</h2>
        <div className="grid gap-8 md:grid-cols-2">
          {features.map((feature, index) => (
            <Feature key={index} {...feature} />
          ))}
        </div>
      </section>
    </>
  );
};

export default Features;
